import { db } from "../lib/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { Kitchen, Menu, MenuItem, KitchenStatus, KitchenSubscriptionStatus } from "../types/index";
import { parseAndValidatePrice, hasValidPrice } from "../utils/priceValidator";
import { resolveItemPhotoUrls } from "../utils/menuImageUrl";

// --- RE-EXPORT TYPES ---
export type { Kitchen, Menu, MenuItem, KitchenStatus, KitchenSubscriptionStatus };

// Timeout configuration
const FIRESTORE_TIMEOUT = 15000; // 15 seconds

// Timeout wrapper
const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number = FIRESTORE_TIMEOUT
): Promise<T> => {
  return Promise.race<T>([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(
        () =>
          reject(
            new Error(
              "Firestore request timeout - please check your connection"
            )
          ),
        timeoutMs
      )
    ),
  ]);
};

// Retry wrapper with exponential backoff
const withRetry = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 2,
  delayMs: number = 1000
): Promise<T> => {
  let lastError: any;

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;

      if (
        error.code === "permission-denied" ||
        error.message?.includes("timeout")
      ) {
        throw error;
      }

      if (attempt <= maxRetries) {
        console.warn(
          `Attempt ${attempt} failed, retrying in ${
            delayMs * attempt
          }ms...`,
          error.message
        );
        await new Promise((resolve) =>
          setTimeout(resolve, delayMs * attempt)
        );
      }
    }
  }

  throw lastError;
};

// --- HELPER: Recursive Deep Clean ---
// Removes undefined values but preserves Arrays and Dates
const cleanData = (data: any): any => {
  if (data === null || data === undefined) return undefined;

  if (typeof data !== "object") return data;

  if (
    data instanceof Date ||
    (data.seconds !== undefined && data.nanoseconds !== undefined)
  ) {
    return data;
  }

  if (Array.isArray(data)) {
    return data
      .map((item) => cleanData(item))
      .filter((item) => item !== undefined);
  }

  const cleaned: any = {};
  Object.keys(data).forEach((key) => {
    const value = cleanData(data[key]);
    if (value !== undefined) {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// --- 1. Public: Get All Active & Open Kitchens ---
export const getOpenKitchens = async (): Promise<Kitchen[]> => {
  try {
    return await withTimeout(
      withRetry(async () => {
        const kitchensRef = collection(db, "kitchens");
        // Admin toggles status as active|paused|blocked; treat active (+ legacy open) as available
        const q = query(
          kitchensRef,
          where("subscriptionStatus", "==", "active")
        );
        const snapshot = await getDocs(q);
        return snapshot.docs
          .map(
            (d) =>
              ({
                ...d.data(),
                id: d.id,
                uid: d.id,
              } as Kitchen)
          )
          .filter((k) => {
            const status = (k.status || "").toLowerCase();
            return status === "open" || status === "active";
          });
      })
    );
  } catch (error: any) {
    console.error("Failed to load kitchens:", error);

    if (error.message?.includes("timeout")) {
      throw new Error(
        "Loading kitchens is taking too long. Please check your internet connection or disable ad blockers."
      );
    }
    if (error.code === "permission-denied") {
      throw new Error(
        "Unable to access kitchen data. Please try logging in again."
      );
    }
    if (error.message?.includes("ERR_BLOCKED_BY_CLIENT")) {
      throw new Error(
        "Your ad blocker is preventing data from loading. Please disable it for this site."
      );
    }

    throw error;
  }
};

// --- 2. Admin: Get ALL Kitchens ---
export const getAllKitchens = async (): Promise<Kitchen[]> => {
  try {
    return await withTimeout(
      withRetry(async () => {
        const kitchensRef = collection(db, "kitchens");
        const snapshot = await getDocs(kitchensRef);
        return snapshot.docs.map(
          (d) =>
            ({
              ...d.data(),
              id: d.id,
              uid: d.id,
            } as Kitchen)
        );
      })
    );
  } catch (error: any) {
    console.error("Failed to load all kitchens:", error);
    throw error;
  }
};

// --- 2.5. Admin: Create Kitchen ---
export const createKitchen = async (
  kitchenData: Partial<Kitchen>
): Promise<string> => {
  const kitchensRef = collection(db, "kitchens");

  const payload = cleanData({
    ...kitchenData,
    createdAt: Timestamp.now(),
    chefUids: kitchenData.chefUids || [],
    status: kitchenData.status || "closed",
    subscriptionStatus:
      kitchenData.subscriptionStatus || "pending_approval",
  });

  const docRef = await addDoc(kitchensRef, payload);
  return docRef.id;
};

// --- 3. Public: Get Menu by Kitchen ID with Price Validation ---
export const getMenuByKitchenId = async (
  kitchenId: string
): Promise<Menu> => {
  try {
    return await withTimeout(
      withRetry(async () => {
        let menuDocRef = doc(db, "menus", kitchenId);
        let menuDocSnap = await getDoc(menuDocRef);

        if (!menuDocSnap.exists()) {
          const cleanId = kitchenId
            .toLowerCase()
            .replace(/^#/, "")
            .replace(/_/g, "-");
          menuDocRef = doc(db, "menus", cleanId);
          menuDocSnap = await getDoc(menuDocRef);
        }

        const menuData = menuDocSnap.exists() ? menuDocSnap.data() : null;

        if (!menuData) {
          const kitchenDocRef = doc(db, "kitchens", kitchenId);
          const kitchenDocSnap = await getDoc(kitchenDocRef);
          const kitchenName = kitchenDocSnap.exists()
            ? kitchenDocSnap.data()?.kitchen_name
            : kitchenId;

          return {
            name: kitchenName || "Menu",
            menuItems: {},
          };
        }

        const validatedMenuItems = Object.entries(
          menuData.menuItems || {}
        ).reduce(
          (acc, [key, item]: [string, any]) => {
            if (hasValidPrice(item)) {
              const withPrice = {
                ...item,
                price: parseAndValidatePrice(
                  item.price ?? item.basePrice
                ),
              };
              // Prefer Hosting copies when Firebase Storage download URLs are dead (e.g. billing closed / 402)
              acc[key] = resolveItemPhotoUrls(withPrice);
            } else {
              console.warn(
                `⚠️ Skipping item ${key} - no valid price`
              );
            }
            return acc;
          },
          {} as Record<string, any>
        );

        return {
          name: menuData.name,
          menuItems: validatedMenuItems,
        } as Menu;
      })
    );
  } catch (error: any) {
    console.error(`Failed to load menu for kitchen ${kitchenId}:`, error);

    if (
      error.message?.includes("timeout") ||
      error.message?.includes("blocked")
    ) {
      console.warn("Returning empty menu due to timeout/block");
      return {
        name: "Menu Unavailable",
        menuItems: {},
      };
    }

    throw error;
  }
};

// Get Kitchen details
export const getKitchenDetails = async (
  kitchenId: string
): Promise<Kitchen | null> => {
  try {
    return await withTimeout(
      withRetry(async () => {
        const ref = doc(db, "kitchens", kitchenId);
        const snap = await getDoc(ref);
        if (snap.exists())
          return { id: snap.id, ...snap.data() } as Kitchen;
        return null;
      })
    );
  } catch (err: any) {
    console.error("Error fetching kitchen details:", err);

    if (err.message?.includes("timeout")) {
      console.warn("Kitchen details timed out, returning null");
      return null;
    }

    return null;
  }
};

// --- 4. Chef: Update Operation Status ---
export const updateKitchenStatus = async (args: {
  kitchenId: string;
  status: KitchenStatus;
}): Promise<KitchenStatus> => {
  const { kitchenId, status } = args;
  if (!status) throw new Error("Status cannot be undefined");

  const kitchenRef = doc(db, "kitchens", kitchenId);
  await updateDoc(kitchenRef, {
    status,
    lastUpdated: Timestamp.now(),
  });
  return status;
};

// --- 5. Admin: Update Subscription Status ---
export const updateKitchenSubscriptionStatus = async (args: {
  kitchenId: string;
  status: KitchenSubscriptionStatus;
}): Promise<void> => {
  const { kitchenId, status } = args;
  if (!status) throw new Error("Subscription Status cannot be undefined");

  const kitchenRef = doc(db, "kitchens", kitchenId);
  await updateDoc(kitchenRef, {
    subscriptionStatus: status,
    lastUpdated: Timestamp.now(),
  });
};

// --- 6. Admin: General Update ---
export const updateKitchen = async (
  kitchenId: string,
  updates: Partial<Kitchen>
): Promise<void> => {
  const kitchenRef = doc(db, "kitchens", kitchenId);

  const cleanedUpdates = cleanData(updates);

  if (!cleanedUpdates || Object.keys(cleanedUpdates).length === 0) {
    console.warn("Skipping update: No valid fields.");
    return;
  }

  await updateDoc(kitchenRef, {
    ...cleanedUpdates,
    lastUpdated: Timestamp.now(),
  });
};

// --- 7. Menu Management ---
export const getChefMenu = async (
  kitchenId: string
): Promise<any[]> => {
  const menu = await getMenuByKitchenId(kitchenId);
  if (!menu.menuItems) return [];
  return Object.entries(menu.menuItems).map(([key, val]) => ({
    ...(val as any),
    id: key,
    chefId: kitchenId,
  }));
};

export const addMenuItem = async (params: {
  groupId: string;
  itemName: string;
  itemObj: any;
}) => {
  const { groupId, itemName, itemObj } = params;
  const menuId = groupId
    .toLowerCase()
    .replace(/^#/, "")
    .replace(/_/g, "-");
  const menuRef = doc(db, "menus", menuId);

  const cleanedItem = cleanData(itemObj);

  await setDoc(
    menuRef,
    {
      menuItems: {
        [itemName]: cleanedItem,
      },
      name: itemObj.kitchenName || "Kitchen Menu",
    },
    { merge: true }
  );
};

export const updateMenuItem = async (params: {
  groupId: string;
  itemName: string;
  itemObj: any;
}) => {
  const { groupId, itemName, itemObj } = params;
  const menuId = groupId
    .toLowerCase()
    .replace(/^#/, "")
    .replace(/_/g, "-");
  const menuRef = doc(db, "menus", menuId);

  const cleanedItem = cleanData(itemObj);

  await setDoc(
    menuRef,
    {
      menuItems: {
        [itemName]: cleanedItem,
      },
    },
    { merge: true }
  );
};
// HARD DELETE - Removes menu item completely from Firestore
export const deleteMenuItem = async (params: {
  groupId: string;
  itemName: string;
}) => {
  const { groupId, itemName } = params;
  const menuId = groupId
    .toLowerCase()
    .replace(/^#/, "")
    .replace(/_/g, "-");
  const menuRef = doc(db, "menus", menuId);
  
  // Get current menu data
  const menuSnap = await getDoc(menuRef);
  if (!menuSnap.exists()) {
    throw new Error("Menu not found");
  }
  
  const menuData = menuSnap.data();
  const menuItems = { ...menuData.menuItems };
  
  // Delete the item from the object
  delete menuItems[itemName];
  
  // Update Firestore with the item removed
  await updateDoc(menuRef, {
    menuItems: menuItems,
  });
};

// Legacy alias for older admin code
export const getKitchensForAdmin = async () => {
  return getAllKitchens();
};

// --- 8. Kitchen Manager: kitchens owned or managed by uid ---
export const getKitchensForManager = async (
  uid: string,
  isAdmin: boolean = false
): Promise<Kitchen[]> => {
  if (!uid) return [];
  const kitchensRef = collection(db, "kitchens");

  if (isAdmin) {
    const snap = await getDocs(kitchensRef);
    return snap.docs
      .map((d) => ({ ...d.data(), id: d.id, uid: d.id } as Kitchen))
      .filter((k) => {
        const s = String(k.status || "").toLowerCase();
        return s !== "blocked";
      });
  }

  const byOwner = await getDocs(query(kitchensRef, where("ownerUid", "==", uid)));
  const byManager = await getDocs(
    query(kitchensRef, where("managerUids", "array-contains", uid))
  );

  const map = new Map<string, Kitchen>();
  for (const d of [...byOwner.docs, ...byManager.docs]) {
    map.set(d.id, { ...d.data(), id: d.id, uid: d.id } as Kitchen);
  }
  return Array.from(map.values());
};
